import { memo, useCallback, useMemo, useState } from 'react'

import { AddCircle, CalendarMonth, ContentCopy, Event, OpenInNew } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  FormHelperText,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { formatISO } from 'date-fns'
import { AnimatePresence, motion } from 'motion/react'
import CopyToClipboard from 'react-copy-to-clipboard'
import { useTranslation } from 'react-i18next'

import { CodeInline } from '@shared/components/MaterialUIEnhancements/CodeInline'
import { popInOut } from '@shared/utils/support'
import {
  buildSubscriptionUrl,
  buildWebcalUrl,
  CALENDAR_TOKEN_SCOPE,
  GOOGLE_CALENDAR_ADD_BY_URL,
} from 'domains/Workspaces/Settings/Calendar/subscriptionUrl'
import { usePersonalAccessTokenCreateMutation } from 'hooks/mutations/identity/User'
import { workspaceSettingsRoute } from 'pages/NWPlatform/Workspaces/Workspace/Settings/Routing'
import { organizationQueryKeys, workspaceQueryKeys } from 'services/queryKeys'

/** Identity's cap on a personal access token's name - it answers 400 for anything longer. */
const NAME_MAX_LENGTH = 256

/** Ties the field to its helper, which is rendered outside it - see the layout note below. */
const NAME_HELPER_ID = 'calendar-link-name-helper'

/** Squares the button off against the field it sits next to - a small outlined input is 40px tall. */
const matchFieldHeight = { height: 40 }

const OverviewRaw = () => {
  const { t } = useTranslation(['common', 'domain'])

  const { organizationId, environmentId, workspaceId } = workspaceSettingsRoute.useParams()

  const [onCallOnly, setOnCallOnly] = useState(false)
  const [nameOverride, setNameOverride] = useState<string | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [createdName, setCreatedName] = useState('')
  const [copied, setCopied] = useState(false)

  const workspaceQuery = useQuery({
    ...workspaceQueryKeys.detail({ workspaceId }),
    select: (data) => ({ name: data.name }),
  })

  const organizationQuery = useQuery({
    ...organizationQueryKeys.detail({ organizationId }),
    select: (data) => ({ name: data.name }),
  })

  /**
   * The name goes on the token, and once the URL is gone it is the only thing left to recognise the link
   * by - which is why the field says so and defaults to naming this workspace, prefixed by its
   * organization: the tokens all land in one personal list, where workspace names from different
   * organizations can read the same. Editable for whoever subscribes from two devices and wants to tell
   * them apart.
   *
   * The organization is a nicety, not a precondition: if its name cannot be read the workspace alone
   * still names the link, rather than holding the whole flow hostage to a second request.
   */
  const organizationName = organizationQuery.data?.name

  const defaultName = organizationName
    ? t('domain:Workspace.Settings.Calendar.linkName', {
        organization: organizationName,
        workspace: workspaceQuery.data?.name ?? '',
      })
    : t('domain:Workspace.Settings.Calendar.linkNameWithoutOrganization', {
        workspace: workspaceQuery.data?.name ?? '',
      })
  // Clipped here rather than left to the field: a workspace name is allowed the full 256 on its own, and
  // the input's maxLength only stops typing - it would hand the default straight through to a 400.
  const name = (nameOverride ?? defaultName).slice(0, NAME_MAX_LENGTH)

  const createMutation = usePersonalAccessTokenCreateMutation()

  /**
   * Every click mints a token. A token's secret exists only in the response that creates it - identity
   * keeps a hash - so an existing calendar token cannot be turned back into a link, and reusing one is
   * not on the table. That leaves them to accumulate, which is why the link is named and the hint below
   * says where they are deleted.
   */
  const handleGenerate = useCallback(() => {
    createMutation.mutate(
      {
        userId: 'my',
        personalAccessTokenInsert: {
          name: name.trim(),
          validFrom: formatISO(new Date()),
          // A feed that stops working is worse than one that lives long: the calendar app fails quietly
          // and the events simply stop appearing. The link is revoked by deleting its token instead.
          expiryTime: null,
          allowFullAccess: false,
          scopes: [CALENDAR_TOKEN_SCOPE],
        },
      },
      {
        onSuccess: (data) => {
          setToken(data.token!)
          setCreatedName(data.name)
          setCopied(false)
        },
      },
    )
  }, [createMutation, name])

  const handleCopy = useCallback(() => {
    setCopied(true)
    setTimeout(() => setCopied(false), 3000)
  }, [])

  const subscriptionUrl = useMemo(
    () => (token ? buildSubscriptionUrl({ environmentId, workspaceId, onCallOnly, token }) : null),
    [environmentId, onCallOnly, token, workspaceId],
  )

  /**
   * Google's dialog has no way to be handed the URL, so the next best thing is that the only step it
   * leaves is a paste. Neither call is awaited before the other: the clipboard write needs the click's
   * user gesture and a popup blocker would kill a window opened after an await.
   */
  const handleAddInGoogle = useCallback(() => {
    if (!subscriptionUrl) {
      return
    }

    navigator.clipboard?.writeText(subscriptionUrl).then(handleCopy, () => {
      // Denied clipboard permission is survivable - the Copy button is right above the dialog link.
    })

    window.open(GOOGLE_CALENDAR_ADD_BY_URL, '_blank', 'noreferrer')
  }, [handleCopy, subscriptionUrl])

  return (
    <Box
      sx={{ width: '100%' }}
      component={motion.div}
      variants={popInOut}
      initial="hidden"
      animate="visible"
      exit="hidden"
    >
      <Typography variant="heading-base">{t('domain:Workspace.Settings.Calendar.title')}</Typography>
      <Typography sx={{ mb: 2 }} variant="text">
        {t('domain:Workspace.Settings.Calendar.description')}
      </Typography>

      <Paper sx={{ bgcolor: 'background.levels.500', p: 1.5, mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <FormControlLabel
              control={<Switch size="small" checked={onCallOnly} onChange={(e) => setOnCallOnly(e.target.checked)} />}
              label={<Typography variant="text">{t('domain:Workspace.Settings.Calendar.onCallOnly')}</Typography>}
            />
            <Typography variant="text-sm" color="text.secondary" component="p">
              {t('domain:Workspace.Settings.Calendar.onCallOnlyDescription')}
            </Typography>
          </Box>

          {/*
           * The helper sits under the pair rather than inside the field: as the field's own helperText it
           * counted towards the field's height, so centring the row left the button hanging half a line
           * below the input it belongs to.
           */}
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <TextField
                sx={{ width: 260 }}
                size="small"
                label={t('domain:Workspace.Settings.Calendar.nameLabel')}
                value={name}
                onChange={(e) => setNameOverride(e.target.value)}
                slotProps={{ htmlInput: { maxLength: NAME_MAX_LENGTH, 'aria-describedby': NAME_HELPER_ID } }}
              />

              <LoadingButton
                sx={matchFieldHeight}
                variant="outlined"
                size="small"
                endIcon={<AddCircle />}
                loadingPosition="end"
                loading={createMutation.isPending}
                disabled={!workspaceQuery.isSuccess || organizationQuery.isLoading || !name.trim()}
                onClick={handleGenerate}
              >
                {t('domain:Workspace.Settings.Calendar.generate')}
              </LoadingButton>
            </Stack>

            {/* Indented by the input's own 14px of padding, so it starts where the field's text does. */}
            <FormHelperText sx={{ mx: 1.75, mt: 0.5 }} id={NAME_HELPER_ID}>
              {t('domain:Workspace.Settings.Calendar.nameHelper')}
            </FormHelperText>
          </Box>
        </Stack>
      </Paper>

      <AnimatePresence>
        {!!subscriptionUrl && (
          <Alert sx={{ mb: 2 }} severity="success" onClose={() => setToken(null)}>
            <Typography variant="heading-sm" sx={{ mb: 0.5 }}>
              {t('domain:Workspace.Settings.Calendar.linkCreated')}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CodeInline sx={{ flex: 1, py: 0.25 }} noWrap>
                {subscriptionUrl}
              </CodeInline>
              <CopyToClipboard text={subscriptionUrl} onCopy={handleCopy}>
                <Tooltip title={t('domain:Workspace.Settings.Calendar.copyLink')}>
                  <Button size="small" startIcon={<ContentCopy fontSize="small" />}>
                    {copied
                      ? t('domain:Workspace.Settings.Calendar.copied')
                      : t('domain:Workspace.Settings.Calendar.copy')}
                  </Button>
                </Tooltip>
              </CopyToClipboard>
            </Box>

            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<CalendarMonth fontSize="small" />}
                endIcon={<OpenInNew fontSize="small" />}
                onClick={handleAddInGoogle}
              >
                {t('domain:Workspace.Settings.Calendar.addToGoogle')}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Event fontSize="small" />}
                href={buildWebcalUrl(subscriptionUrl)}
              >
                {t('domain:Workspace.Settings.Calendar.addToDefaultApp')}
              </Button>
            </Stack>

            <Typography variant="text-sm" component="p" sx={{ mb: 0.5 }}>
              {t('domain:Workspace.Settings.Calendar.googleHint')}
            </Typography>
            <Typography variant="text-sm" component="p" sx={{ mb: 0.5 }}>
              {t('domain:Workspace.Settings.Calendar.linkIsACredential')}
            </Typography>
            <Typography variant="text-sm">
              {t('domain:Workspace.Settings.Calendar.tokenHint', { name: createdName })}
            </Typography>
          </Alert>
        )}
      </AnimatePresence>

      {createMutation.isError && <Alert severity="error">{t('somethingWentWrong')}</Alert>}
    </Box>
  )
}

export const Overview = memo(OverviewRaw)
